/**
 * Google Analytics 4 (GA4) utility functions for admin panel
 * Tracks events, page views, and admin panel usage
 * Excludes SUPERADMIN actions from analytics (tracks STORE_ADMIN/STORE_MANAGER)
 */

declare global {
  interface Window {
    gtag?: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      config?: Record<string, unknown>
    ) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Initialize Google Analytics
 * Should be called once when the app loads
 */
export const initAnalytics = (): void => {
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;

  if (!measurementId) {
    // Analytics is optional - don't break the app if not configured
    return;
  }

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];
  const dataLayer = window.dataLayer;
  window.gtag = function gtag(...args: unknown[]) {
    dataLayer.push(args);
  };

  // Load GA4 script
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  
  // Add error handling for script loading
  script.onerror = () => {
    console.error('Google Analytics: Failed to load gtag.js script');
  };
  
  document.head.appendChild(script);

  // Configure GA4 (commands are queued in dataLayer until script loads)
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    page_path: window.location.pathname + window.location.search,
    page_location: window.location.href,
    send_page_view: true,
  });
};

/**
 * Track a custom event
 * Excludes SUPERADMIN actions (tracks STORE_ADMIN/STORE_MANAGER)
 */
export const trackEvent = (
  eventName: string,
  eventParams?: Record<string, string | number | boolean>,
  userRole?: string
): void => {
  // Skip analytics for SUPERADMIN
  if (userRole === 'SUPERADMIN') {
    return;
  }

  if (!window.gtag) {
    return;
  }

  window.gtag('event', eventName, eventParams);
};

/**
 * Track page view
 * Excludes SUPERADMIN actions (tracks STORE_ADMIN/STORE_MANAGER)
 */
export const trackPageView = (path: string, userRole?: string): void => {
  // Skip analytics for SUPERADMIN
  if (userRole === 'SUPERADMIN') {
    return;
  }

  if (!window.gtag) {
    return;
  }

  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  if (!measurementId) {
    return;
  }

  window.gtag('config', measurementId, {
    page_path: path,
    page_location: window.location.origin + path,
    send_page_view: true,
  });
};

/**
 * Track feature usage in admin panel
 */
export const trackFeatureUsage = (
  featureName: string,
  action: string,
  userRole?: string
): void => {
  trackEvent('feature_usage', {
    feature_name: featureName,
    action,
    event_category: 'admin_panel',
  }, userRole);
};

